import { useAuthContext } from '@classroom-ai/ui/auth/hooks';
import { Iconify } from '@classroom-ai/ui/components/iconify';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';

import { useActiveClass } from 'src/class/class-provider';

import { useAskAi } from './ask-ai-provider';
import { shortcutHint } from './beacon-shortcut';

// ----------------------------------------------------------------------

/**
 * Header entry point for Beacon — filled purple, the colour of every AI surface (epic #617),
 * with the keyboard shortcut on it so teachers learn it (Ask AI, epic #391; renamed in #577), scoped to the active class. Hidden without
 * one — a class-less Department Head or a DISTRICT_ADMIN (no class context; same rule as
 * the context switchers). The drawer itself lives in `AskAiProvider`.
 */
export function AskAiButton() {
  const { user } = useAuthContext();
  const { activeClass } = useActiveClass();
  const { openAskAi } = useAskAi();

  if (user?.role === 'DISTRICT_ADMIN' || !activeClass) return null;

  return (
    <>
      <Button
        variant="contained"
        color="secondary"
        startIcon={<Iconify icon="solar:stars-bold-duotone" width={20} />}
        onClick={() => openAskAi()}
        aria-keyshortcuts="/ Meta+K Control+K"
        sx={{
          whiteSpace: 'nowrap',
          flexShrink: 0,
          display: { xs: 'none', sm: 'inline-flex' },
          boxShadow: (theme) => `0 8px 16px -6px ${theme.vars.palette.secondary.main}`,
        }}
      >
        Ask Beacon
        <Box
          component="kbd"
          sx={{
            ml: 1,
            px: 0.75,
            borderRadius: 0.75,
            typography: 'caption',
            fontFamily: 'inherit',
            fontWeight: 600,
            bgcolor: 'rgba(255,255,255,0.2)',
            display: { xs: 'none', md: 'inline' },
          }}
        >
          {shortcutHint()}
        </Box>
      </Button>
      <IconButton
        aria-label="Ask Beacon"
        onClick={() => openAskAi()}
        sx={{
          flexShrink: 0,
          display: { xs: 'inline-flex', sm: 'none' },
          color: 'common.white',
          bgcolor: 'secondary.main',
          borderRadius: 1.5,
          '&:hover': { bgcolor: 'secondary.dark' },
        }}
      >
        <Iconify icon="solar:stars-bold-duotone" width={20} />
      </IconButton>
    </>
  );
}
